import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import patientPortalApi from "../../api/patientPortalApi";
import { setToken } from "../../utils/tokenManager";
import { LoginWrapper, LoginCard } from "./PatientPortal.styles";

export default function PatientPortalLogin() {
  const navigate = useNavigate();

  // Estados del formulario
  const [step, setStep] = useState(1); // 1: Documento, 2: Contraseña (crear o login)
  const [identificacion, setIdentificacion] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [patientInfo, setPatientInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  // Paso 1: Validar Documento
  const handleCheckDocument = async (e) => {
    e.preventDefault();
    if (!identificacion.trim()) {
      toast.warning("Por favor ingresa tu número de identificación");
      return;
    }

    try {
      setLoading(true);
      const res = await patientPortalApi.checkDocument(identificacion.trim());

      if (res.success && res.data) {
        setPatientInfo(res.data);
        setStep(2);
      } else {
        toast.error(res.message || "Paciente no encontrado o inactivo");
      }
    } catch (error) {
      console.error("Error al validar documento:", error);
      toast.error(
        error.response?.data?.message ||
          "No se encontró un paciente activo con el documento ingresado"
      );
    } finally {
      setLoading(false);
    }
  };

  // Paso 2A: Crear Contraseña (Primer ingreso)
  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.warning("Ingresa una contraseña");
      return;
    }
    if (password.length < 6) {
      toast.warning("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    if (password !== confirmPassword) {
      toast.warning("Las contraseñas no coinciden");
      return;
    }

    try {
      setLoading(true);
      const res = await patientPortalApi.setPassword(identificacion.trim(), password);

      if (res.success && res.data?.access_token) {
        setToken(res.data.access_token);
        sessionStorage.setItem("patient_token", res.data.access_token);
        sessionStorage.setItem("patient_user", JSON.stringify(res.data.user));
        toast.success("¡Contraseña creada con éxito! Bienvenido.");
        navigate("/portal-paciente", { replace: true });
      } else {
        toast.error(res.message || "Error al crear la contraseña");
      }
    } catch (error) {
      console.error("Error al crear contraseña:", error);
      toast.error(error.response?.data?.message || "Error al configurar la contraseña");
    } finally {
      setLoading(false);
    }
  };

  // Paso 2B: Iniciar Sesión (Paciente ya registrado)
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.warning("Por favor ingresa tu contraseña");
      return;
    }

    try {
      setLoading(true);
      const res = await patientPortalApi.login(identificacion.trim(), password);

      if (res.success && res.data?.access_token) {
        setToken(res.data.access_token);
        sessionStorage.setItem("patient_token", res.data.access_token);
        sessionStorage.setItem("patient_user", JSON.stringify(res.data.user));
        toast.success(`Bienvenido de nuevo, ${res.data.user?.nombre || ""}`);
        navigate("/portal-paciente", { replace: true });
      } else {
        toast.error(res.message || "Contraseña incorrecta");
      }
    } catch (error) {
      console.error("Error al iniciar sesión:", error);
      toast.error(error.response?.data?.message || "Contraseña incorrecta o cuenta inactiva");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setStep(1);
    setPassword("");
    setConfirmPassword("");
    setPatientInfo(null);
  };

  return (
    <LoginWrapper>
      <LoginCard>
        <div className="login-header">
          <h1>APIM Paciente</h1>
          <p>Consulta tus actividades y resultados de salud</p>
        </div>

        {/* PASO 1: Ingreso de Cédula / Documento */}
        {step === 1 && (
          <form onSubmit={handleCheckDocument}>
            <div className="form-group">
              <label htmlFor="docInput">Número de Identificación</label>
              <input
                id="docInput"
                type="text"
                placeholder="Ej. 1050002561"
                value={identificacion}
                onChange={(e) => setIdentificacion(e.target.value)}
                disabled={loading}
                autoFocus
              />
            </div>

            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? "Verificando..." : "Continuar"}
            </button>
          </form>
        )}

        {/* PASO 2A: Crear Contraseña Inicial */}
        {step === 2 && !patientInfo?.registrado && (
          <form onSubmit={handleSetPassword}>
            <div className="info-alert">
              <strong>Hola, {patientInfo?.nombre}</strong>
              <br />
              Es tu primer ingreso al portal. Configura tu contraseña para acceder de
              forma segura.
            </div>

            <div className="form-group">
              <label htmlFor="newPass">Crear Contraseña</label>
              <input
                id="newPass"
                type="password"
                placeholder="Mínimo 6 caracteres"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                autoFocus
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPass">Confirmar Contraseña</label>
              <input
                id="confirmPass"
                type="password"
                placeholder="Repite la contraseña"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
              />
            </div>

            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? "Guardando..." : "Crear Contraseña e Ingresar"}
            </button>

            <button
              type="button"
              className="btn-back"
              onClick={handleReset}
              disabled={loading}
            >
              ← Consultar otro documento
            </button>
          </form>
        )}

        {/* PASO 2B: Iniciar Sesión con Contraseña */}
        {step === 2 && patientInfo?.registrado && (
          <form onSubmit={handleLogin}>
            <div className="info-alert">
              <strong>Hola, {patientInfo?.nombre}</strong>
              <br />
              Ingresa tu contraseña para acceder a tus actividades de salud.
            </div>

            <div className="form-group">
              <label htmlFor="loginPass">Contraseña</label>
              <input
                id="loginPass"
                type="password"
                placeholder="Ingresa tu contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                autoFocus
              />
            </div>

            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? "Iniciando sesión..." : "Iniciar Sesión"}
            </button>

            <button
              type="button"
              className="btn-back"
              onClick={handleReset}
              disabled={loading}
            >
              ← Consultar otro documento
            </button>
          </form>
        )}
      </LoginCard>
    </LoginWrapper>
  );
}

